import React from 'react';
import { 
  Activity, CheckCircle, PlayCircle, UserPlus, 
  MessageSquare, FileText, RefreshCw
} from 'lucide-react';
import { ACTIVITY_TYPES } from '../constants';

const ActivityLog = ({ activities = [], loading = false }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case ACTIVITY_TYPES.STEP_COMPLETED:
        return <CheckCircle size={16} className="text-green-500" />;
      case ACTIVITY_TYPES.STEP_STARTED:
        return <PlayCircle size={16} className="text-blue-500" />;
      case ACTIVITY_TYPES.USER_ASSIGNED:
        return <UserPlus size={16} className="text-purple-500" />;
      case ACTIVITY_TYPES.NOTE_ADDED:
        return <MessageSquare size={16} className="text-teal-500" />;
      case ACTIVITY_TYPES.DOCUMENT_UPLOADED:
        return <FileText size={16} className="text-orange-500" />;
      case ACTIVITY_TYPES.STATUS_CHANGE:
        return <RefreshCw size={16} className="text-indigo-500" />;
      default:
        return <Activity size={16} className="text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={20} className="text-gray-400" />
          <h3 className="font-semibold text-gray-800">Activity Log</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2 mt-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={20} className="text-teal-600" />
        <h3 className="font-semibold text-gray-800">Activity Log</h3>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-8">
          <Activity size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No activity recorded yet</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities.map((activity, index) => (
            <div key={activity.id || index} className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800">{activity.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">{formatDate(activity.createdAt)}</span>
                  {activity.userName && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500">{activity.userName}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
